import Image from '@base/Image';
import styled from '@emotion/styled';
import { useIsMobile } from '@stores/AppCommonData';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { getRegularDate } from '@utils/dates';
import Typography from 'aquilla/core/atoms/Typography';
import React, { forwardRef } from 'react';
import useTestSeriesInfo from './useTestSeriesInfo';
import MapsLink from './MapsLink';

const Container = styled.div`
  margin-top: ${Spacings.SPACING_14B};

  ${mediaQuery.uptoMobile} {
    margin-top: 0;
    padding-top: ${Spacings.SPACING_4B};
  }
`;

const Title = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_6B};

  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_4B};
  }
`;

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_6B};
`;

const DetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${Spacings.SPACING_3B};
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FieldRow = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_6B};
  align-items: flex-start;
`;

const FieldLabel = styled(Typography)`
  font-size: ${Fonts.ULTRA_SMALL};
  line-height: ${Spacings.SPACING_4B};
  color: var(--color-text-secondary);
`;

const FieldValue = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.H6};
  line-height: 18px;
  color: var(--color-text-primary);
`;

const CentreFieldGroup = styled(FieldGroup)`
  flex: 1;
  min-width: 0;
`;

const CentreAddress = styled.div`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.H6};
  line-height: 18px;
  color: var(--color-text-primary);
`;

const AdmitCardCard = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
  overflow: hidden;

  ${mediaQuery.uptoMobile} {
    flex-direction: column;
  }
`;

const AdmitCardContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_4B};
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
  min-width: 0;

  ${mediaQuery.uptoMobile} {
    align-items: flex-start;
    padding: ${Spacings.SPACING_4B};
  }
`;

const AdmitCardIcon = styled.div`
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 7px;
  overflow: hidden;
`;

const AdmitCardText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const AdmitCardMessage = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.H6};
  line-height: 18px;
  color: var(--color-text-primary);
`;

const AdmitCardDate = styled(Typography)`
  font-size: ${Fonts.ULTRA_SMALL};
  line-height: ${Spacings.SPACING_4B};
  color: var(--color-text-secondary);
`;

const DownloadButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${Spacings.SPACING_4B};

  ${mediaQuery.uptoMobile} {
    padding: 0 ${Spacings.SPACING_4B} ${Spacings.SPACING_4B};
  }
`;

const DownloadActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacings.SPACING_2B};
  width: 192px;
  height: ${Spacings.SPACING_12B};
  padding: 0 ${Spacings.SPACING_4B};
  border: none;
  border-radius: ${Spacings.SPACING_2B};
  background-color: var(--color-text-primary);
  color: var(--color-base-1);
  font-weight: ${Fonts.BOLD};
  font-size: ${Fonts.H6};
  line-height: 18px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const DownloadIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const EnrollmentDetails = forwardRef((_props, ref) => {
  const isMobile = useIsMobile();
  const { data } = useTestSeriesInfo();
  const { enrolmentDetails } = data;

  if (!enrolmentDetails?.admitCardUrl) {
    return null;
  }

  const {
    city,
    state,
    centreName,
    centreAddress,
    centrePinCode,
    centreGoogleMapsLink,
    rollNumber,
    admitCardUrl,
    admitCardCreatedAt
  } = enrolmentDetails;

  const centreDisplayAddress =
    centreAddress ||
    [centreName, city, state, centrePinCode].filter(Boolean).join(', ');

  const createdOnLabel = admitCardCreatedAt
    ? `Created on ${getRegularDate(admitCardCreatedAt)}`
    : null;

  const handleDownload = () => {
    if (admitCardUrl) {
      window.open(admitCardUrl, '_blank');
    }
  };

  return (
    <Container ref={ref}>
      {!isMobile && <Title variant="h4">Enrolment Details</Title>}

      <DetailsSection>
        <DetailRow>
          <IconWrapper>
            <Image
              src="allCentre/allowLocation.svg"
              width={40}
              height={40}
              alt=""
            />
          </IconWrapper>
          <FieldRow>
            <FieldGroup>
              <FieldLabel variant="p3">City</FieldLabel>
              <FieldValue variant="p3">{city}</FieldValue>
            </FieldGroup>
            <FieldGroup>
              <FieldLabel variant="p3">State</FieldLabel>
              <FieldValue variant="p3">{state}</FieldValue>
            </FieldGroup>
          </FieldRow>
        </DetailRow>

        <DetailRow>
          <IconWrapper>
            <Image
              src="offline-centres/centre-Illustration.svg"
              width={40}
              height={40}
              alt=""
            />
          </IconWrapper>
          <CentreFieldGroup>
            <FieldLabel variant="p3">Test centre</FieldLabel>
            <CentreAddress>{centreDisplayAddress}</CentreAddress>
            <MapsLink href={centreGoogleMapsLink} />
          </CentreFieldGroup>
        </DetailRow>

        {rollNumber && (
          <DetailRow>
            <IconWrapper>
              <Image
                src="offline-centres/postpayment/light-checkmark.svg"
                width={40}
                height={40}
                alt=""
              />
            </IconWrapper>
            <FieldGroup>
              <FieldLabel variant="p3">Roll number</FieldLabel>
              <FieldValue variant="p3">{rollNumber}</FieldValue>
            </FieldGroup>
          </DetailRow>
        )}
      </DetailsSection>

      <AdmitCardCard>
        <AdmitCardContent>
          <AdmitCardIcon>
            <Image
              src="/test-series-revamp/admit-card-thumbnail.svg"
              width={56}
              height={56}
              alt=""
            />
          </AdmitCardIcon>
          <AdmitCardText>
            <AdmitCardMessage variant="p3">
              Please carry a hard copy of your admit card for the test.
            </AdmitCardMessage>
            {createdOnLabel && (
              <AdmitCardDate variant="p3">{createdOnLabel}</AdmitCardDate>
            )}
          </AdmitCardText>
        </AdmitCardContent>
        <DownloadButtonWrapper>
          <DownloadActionButton
            type="button"
            onClick={handleDownload}
            disabled={!admitCardUrl}
          >
            <DownloadIconWrapper>
              <Image
                src="/test-series-revamp/download-icon.svg"
                width={16}
                height={16}
                alt=""
              />
            </DownloadIconWrapper>
            Download
          </DownloadActionButton>
        </DownloadButtonWrapper>
      </AdmitCardCard>
    </Container>
  );
});

EnrollmentDetails.displayName = 'EnrollmentDetails';

export default React.memo(EnrollmentDetails);
